import React, { useEffect, useState } from 'react'
import { Modal } from 'react-bootstrap'
import { getCommissionRuleAudit } from '../../core/api'
import { IAuditEntry } from '../../core/response.types'

interface Props {
  show:    boolean
  ruleId:  string | null
  onClose: () => void
}

const tryFormat = (v: unknown): string => {
  try { return JSON.stringify(v, null, 2) } catch { return String(v) }
}

const CommissionRuleAuditModal: React.FC<Props> = ({ show, ruleId, onClose }) => {
  const [entries, setEntries]     = useState<IAuditEntry[]>([])
  const [loading, setLoading]     = useState(false)
  const [nextCursor, setNextCur]  = useState<string | null>(null)
  const [stack, setStack]         = useState<string[]>([])

  const load = async (cursor?: string) => {
    if (!ruleId) return
    setLoading(true)
    try {
      const resp = await getCommissionRuleAudit(ruleId, cursor ? { cursor } : {})
      if (!resp.data?.success) {
        setEntries([])
        setNextCur(null)
        return
      }
      setEntries(resp.data.data ?? [])
      setNextCur(resp.data.nextCursor ?? null)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (show && ruleId) {
      setStack([])
      load(undefined)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, ruleId])

  if (!ruleId) return null

  return (
    <Modal show={show} onHide={onClose} size='lg' centered>
      <div className='modal-header'>
        <h3 className='modal-title'>История правила {ruleId}</h3>
      </div>
      <div className='modal-body' style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {loading && <div>Загрузка…</div>}
        {!loading && entries.length === 0 && <div className='text-muted'>Нет записей</div>}
        {entries.map((e) => (
          <div key={e.id} className='border rounded p-3 mb-3'>
            <div className='d-flex justify-content-between'>
              <span className='badge badge-light-primary'>{e.action}</span>
              <span className='text-muted'>{e.changedAt}</span>
            </div>
            <div className='mt-1'>Актор: <strong>{e.actor ?? '—'}</strong></div>
            <details className='mt-2'>
              <summary>Before</summary>
              <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{tryFormat(e.before)}</pre>
            </details>
            <details>
              <summary>After</summary>
              <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{tryFormat(e.after)}</pre>
            </details>
          </div>
        ))}
        <div className='d-flex justify-content-end gap-2'>
          <button
            type='button'
            className='btn btn-sm btn-light'
            disabled={stack.length === 0 || loading}
            onClick={() => {
              const next = [...stack]
              const last = next.pop() ?? ''
              setStack(next)
              load(last || undefined)
            }}
          >
            &lt; Назад
          </button>
          <button
            type='button'
            className='btn btn-sm btn-light'
            disabled={!nextCursor || loading}
            onClick={() => {
              setStack((s) => [...s, ''])
              if (nextCursor) load(nextCursor)
            }}
          >
            Далее &gt;
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default CommissionRuleAuditModal
